import React, { useEffect, useMemo, useState, useId, useCallback } from 'react';
import { Card, CardContent, Typography, Box, alpha, SvgIconProps, IconButton, Tooltip as MuiTooltip, useTheme } from '@mui/material';
import { PushPin, PushPinOutlined } from '@mui/icons-material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTimezone } from '../../contexts/TimezoneContext';
import { formatChartAxisTime, formatUiTimeWithSeconds } from '../../utils/dateUtils';
import {
  computeSeriesWindowStats,
  interpolateAtTimestamp,
  resolveChartClickTimestamp,
  type ChartClickState,
} from '../../utils/timeseriesStats';

export interface TimeseriesPoint {
  timestamp: number;
  value: number;
}

export interface TimeseriesSeries {
  id: string;
  label: string;
  color: string;
  data: TimeseriesPoint[];
  type?: 'line' | 'area';
  strokeWidth?: number;
}

type MergedPoint = { timestamp: number } & Record<string, number | null>;

const defaultNumberFormatter = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—';

const DefaultTooltip = ({
  active,
  payload,
  label,
  valueFormatter = defaultNumberFormatter,
  labelFormatter,
  timeZone,
}: {
  active?: boolean;
  payload?: any[];
  label?: unknown;
  valueFormatter?: (value: number) => string;
  labelFormatter?: (label: unknown) => string;
  timeZone: string;
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  if (!active || !payload || payload.length === 0) return null;

  const labelText =
    typeof labelFormatter === 'function'
      ? labelFormatter(label)
      : formatChartAxisTime(label as number | string | Date, timeZone);

  return (
    <Box
      sx={{
        backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.95 : 0.98),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
        borderRadius: 1,
        p: 1.5,
        backdropFilter: 'blur(12px)',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Space Mono", monospace',
          fontSize: '0.85rem',
          color: theme.palette.text.secondary,
          mb: 0.75,
        }}
      >
        {labelText}
      </Typography>

      <Box sx={{ display: 'grid', gap: 0.5 }}>
        {payload.map((p: any) => {
          const seriesColor = p.stroke || p.color || theme.palette.primary.main;
          return (
          <Box key={p.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: seriesColor,
                boxShadow: `0 0 10px ${seriesColor}66`,
              }}
            />
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.85rem',
                color: theme.palette.text.secondary,
                flex: 1,
              }}
            >
              {p.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.95rem',
                color: seriesColor,
                fontWeight: 700,
              }}
            >
              {valueFormatter(p.value)}
            </Typography>
          </Box>
        );
        })}
      </Box>
    </Box>
  );
};

const mergeSeries = (series: TimeseriesSeries[]): MergedPoint[] => {
  const rows = new Map<number, MergedPoint>();

  for (const s of series) {
    for (const p of s.data) {
      const row = rows.get(p.timestamp) || ({ timestamp: p.timestamp } as MergedPoint);
      row[s.id] = p.value;
      rows.set(p.timestamp, row);
    }
  }

  return Array.from(rows.values()).sort((a, b) => a.timestamp - b.timestamp);
};

export const TimeseriesCard = React.memo(
  ({
    title,
    subtitle,
    icon: Icon,
    unit,
    height = 200,
    series,
    xDomain,
    yDomain,
    yAxisPadding,
    isPinned,
    onTogglePin,
    yTickFormatter,
    yAxisAllowDecimals = true,
    valueFormatter = defaultNumberFormatter,
    /** Stack area series on top of one another instead of overlaying them. */
    stacked = false,
    /** Show a summed "Total" of all visible series in the header and legend. */
    showTotal = false,
    /** When set (e.g. execution log row hover), draw a sync line and show interpolated values at this time. */
    referenceTimeMs = null,
    /**
     * When provided, clicking a point on the chart reports its timestamp so the caller
     * can lock the shared cursor across every chart. Called with the clicked point's
     * time in ms, or null when the click did not land on a data point.
     */
    onSelectReferenceTime,
    /** True when `referenceTimeMs` is pinned rather than following the pointer. */
    referenceLocked = false,
  }: {
    title: string;
    subtitle?: string;
    icon?: React.ComponentType<SvgIconProps>;
    unit?: string;
    height?: number;
    series: TimeseriesSeries[];
    xDomain?: [number, number];
    yDomain?: [number, number];
    yAxisPadding?: { top?: number; bottom?: number };
    isPinned?: boolean;
    onTogglePin?: () => void;
    yTickFormatter?: (value: number) => string;
    yAxisAllowDecimals?: boolean;
    valueFormatter?: (value: number) => string;
    stacked?: boolean;
    showTotal?: boolean;
    referenceTimeMs?: number | null;
    onSelectReferenceTime?: (timeMs: number | null) => void;
    referenceLocked?: boolean;
  }) => {
    const theme = useTheme();
    const { timezone } = useTimezone();
    const isDark = theme.palette.mode === 'dark';
    const gradientPrefix = useId();
    const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});

    const axisTimeFormatter = useCallback(
      (value: number) => formatChartAxisTime(value, timezone),
      [timezone]
    );

    // Prune hidden state when series set changes
    useEffect(() => {
      const ids = new Set(series.map((s) => s.id));
      setHiddenSeries((prev) => {
        const next: Record<string, boolean> = {};
        for (const [id, hidden] of Object.entries(prev)) {
          if (hidden && ids.has(id)) next[id] = true;
        }
        return next;
      });
    }, [series]);

    const visibleSeries = useMemo(
      () => series.filter((s) => !hiddenSeries[s.id]),
      [hiddenSeries, series]
    );

    const merged = useMemo(() => mergeSeries(visibleSeries), [visibleSeries]);
    const filteredMerged = useMemo(() => {
      if (!xDomain) return merged;
      const [start, end] = xDomain;
      return merged.filter((row) => row.timestamp >= start && row.timestamp <= end);
    }, [merged, xDomain]);
    const mergedWithBaseline = useMemo(() => {
      if (!xDomain) return filteredMerged;
      const [start, end] = xDomain;

      const makeZeroRow = (ts: number): MergedPoint =>
        visibleSeries.reduce((acc, s) => { acc[s.id] = 0; return acc; }, { timestamp: ts } as MergedPoint);

      if (filteredMerged.length === 0) {
        return [makeZeroRow(start), makeZeroRow(end)];
      }

      let result = filteredMerged as MergedPoint[];
      if (result[0].timestamp > start) {
        // Two zero anchors: one at domain start and one 1 ms before the first real point.
        // This keeps the line flat at zero right up to where data begins, avoiding
        // the "hill" that monotone interpolation would otherwise produce.
        result = [makeZeroRow(start), makeZeroRow(result[0].timestamp - 1), ...result];
      }
      return result;
    }, [filteredMerged, visibleSeries, xDomain]);

    const emptySeriesIds = useMemo(() => {
      return visibleSeries
        .filter((s) => {
          if (!xDomain) return s.data.length === 0;
          const [start, end] = xDomain;
          return !s.data.some((p) => p.timestamp >= start && p.timestamp <= end);
        })
        .map((s) => s.id);
    }, [visibleSeries, xDomain]);

    const mergedWithEmptySeries = useMemo(() => {
      if (mergedWithBaseline.length === 0 || emptySeriesIds.length === 0) return mergedWithBaseline;
      return mergedWithBaseline.map((row) => {
        const next = { ...row } as MergedPoint;
        for (const id of emptySeriesIds) {
          if (next[id] === undefined || next[id] === null) next[id] = 0;
        }
        return next;
      });
    }, [mergedWithBaseline, emptySeriesIds]);

    const lastValueById = useMemo(() => {
      const out: Record<string, number | null> = {};
      const last = mergedWithEmptySeries[mergedWithEmptySeries.length - 1];
      for (const s of series) {
        const value = last ? last[s.id] : null;
        out[s.id] = typeof value === 'number' ? value : null;
      }
      return out;
    }, [mergedWithEmptySeries, series]);

    const latestValues = useMemo(() => {
      const last = mergedWithEmptySeries[mergedWithEmptySeries.length - 1];
      if (!last) return [];
      return visibleSeries.map((s) => ({
        label: s.label,
        color: s.color,
        value: typeof last[s.id] === 'number' ? (last[s.id] as number) : null,
      }));
    }, [mergedWithEmptySeries, visibleSeries]);

    const referenceInDomain = useMemo(() => {
      if (referenceTimeMs == null || !Number.isFinite(referenceTimeMs) || !xDomain) return false;
      const [a, b] = xDomain;
      return referenceTimeMs >= a && referenceTimeMs <= b;
    }, [referenceTimeMs, xDomain]);

    const referenceDisplayValues = useMemo(() => {
      if (!referenceInDomain || referenceTimeMs == null) return null;
      return visibleSeries.map((s) => ({
        label: s.label,
        color: s.color,
        value: interpolateAtTimestamp(s.data, referenceTimeMs),
      }));
    }, [referenceInDomain, referenceTimeMs, visibleSeries]);

    const headerMetricValues = referenceDisplayValues ?? latestValues;

    // Summed "Total" of the currently-visible series (respects legend toggling and
    // the hovered reference time), shown alongside the per-series header counts.
    const headerTotal = useMemo(() => {
      if (!showTotal) return null;
      const vals = headerMetricValues.filter((v) => v.value !== null);
      if (vals.length === 0) return null;
      return vals.reduce((sum, v) => sum + (v.value as number), 0);
    }, [showTotal, headerMetricValues]);

    const legendValueById = useMemo(() => {
      if (referenceInDomain && referenceTimeMs != null) {
        const out: Record<string, number | null> = {};
        for (const s of series) {
          out[s.id] = interpolateAtTimestamp(s.data, referenceTimeMs);
        }
        return out;
      }
      return lastValueById;
    }, [referenceInDomain, referenceTimeMs, series, lastValueById]);

    const legendTotal = useMemo(() => {
      if (!showTotal) return null;
      let sum = 0;
      let any = false;
      for (const s of visibleSeries) {
        const v = legendValueById[s.id];
        if (typeof v === 'number') {
          sum += v;
          any = true;
        }
      }
      return any ? sum : null;
    }, [showTotal, visibleSeries, legendValueById]);

    // Max / average of every visible series over the charted window. Computed from the
    // raw series (not the merged rows) so the synthetic zero baseline anchors added for
    // the x-domain cannot drag the average down.
    const windowStats = useMemo(
      () =>
        visibleSeries.map((s) => ({
          id: s.id,
          label: s.label,
          color: s.color,
          ...computeSeriesWindowStats(s.data, xDomain),
        })),
      [visibleSeries, xDomain]
    );

    const referenceTimeLabel = useMemo(() => {
      if (!referenceInDomain || referenceTimeMs == null) return null;
      return formatUiTimeWithSeconds(referenceTimeMs, timezone);
    }, [referenceInDomain, referenceTimeMs, timezone]);

    const handleChartClick = useCallback(
      (state: ChartClickState) => {
        if (!onSelectReferenceTime) return;
        onSelectReferenceTime(resolveChartClickTimestamp(state));
      },
      [onSelectReferenceTime]
    );

    // recharts delivers its own onClick asynchronously (one animation frame later), so
    // stop the click here -- synchronously -- to keep page-level "click anywhere to
    // dismiss" handlers from clearing the cursor this very click is about to lock.
    const handleChartWrapperClick = useCallback(
      (event: React.MouseEvent) => {
        if (!onSelectReferenceTime) return;
        event.stopPropagation();
      },
      [onSelectReferenceTime]
    );

    const hasArea = visibleSeries.some((s) => (s.type || 'area') === 'area');
    const ChartComponent = hasArea ? AreaChart : LineChart;

    const toggleSeries = (id: string) => {
      setHiddenSeries((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    return (
      <Card
        sx={{
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
          backdropFilter: 'blur(12px)',
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.2)}`,
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.9)}, transparent)`,
          },
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {Icon && (
                <Box
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.18),
                    borderRadius: '10px',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.25 : 0.3)}`,
                  }}
                >
                  <Icon sx={{ color: theme.palette.primary.main, fontSize: '1.15rem' }} />
                </Box>
              )}

              <Box>
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.9rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.85rem',
                      color: theme.palette.text.secondary,
                      mt: 0.25,
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
                {referenceTimeLabel && (
                  <Typography
                    data-testid="timeseries-reference-time"
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.75rem',
                      color: theme.palette.warning.main,
                      mt: 0.25,
                    }}
                  >
                    {referenceLocked ? 'LOCKED @ ' : '@ '}
                    {referenceTimeLabel}
                  </Typography>
                )}
              </Box>
            </Box>

            {(headerMetricValues.length > 0 || onTogglePin) && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 0.5,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {headerMetricValues.length > 0 && (
                    <Box
                      data-testid="timeseries-header-values"
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        gap: headerMetricValues.length <= 2 ? 1 : 0.75,
                        maxWidth: headerMetricValues.length <= 2 ? 'none' : 280,
                      }}
                    >
                      {headerMetricValues.map((v) => (
                        <Box key={v.label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Space Mono", monospace',
                              fontSize: headerMetricValues.length <= 2 ? '0.85rem' : '0.75rem',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {v.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: headerMetricValues.length <= 2 ? '1.1rem' : '0.95rem',
                              color: v.color,
                              fontWeight: 700,
                              textShadow: `0 0 10px ${alpha(v.color, 0.4)}`,
                            }}
                          >
                            {v.value === null ? '—' : valueFormatter(v.value)}
                            {unit ? (
                              <Box component="span" sx={{ fontSize: '0.85rem', ml: 0.5, opacity: 0.75 }}>
                                {unit}
                              </Box>
                            ) : null}
                          </Typography>
                        </Box>
                      ))}
                      {headerTotal !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography
                            sx={{
                              fontFamily: '"Space Mono", monospace',
                              fontSize: headerMetricValues.length <= 2 ? '0.85rem' : '0.75rem',
                              color: theme.palette.text.secondary,
                            }}
                          >
                            Total
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: '"JetBrains Mono", monospace',
                              fontSize: headerMetricValues.length <= 2 ? '1.1rem' : '0.95rem',
                              color: theme.palette.text.primary,
                              fontWeight: 700,
                              textShadow: `0 0 10px ${alpha(theme.palette.text.primary, 0.3)}`,
                            }}
                          >
                            {valueFormatter(headerTotal)}
                            {unit ? (
                              <Box component="span" sx={{ fontSize: '0.85rem', ml: 0.5, opacity: 0.75 }}>
                                {unit}
                              </Box>
                            ) : null}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
                {onTogglePin && (
                  <MuiTooltip title={isPinned ? 'Unpin from top' : 'Pin to top'}>
                    <IconButton
                      size="small"
                      onClick={onTogglePin}
                      sx={{
                        color: isPinned ? theme.palette.primary.main : theme.palette.text.secondary,
                        border: `1px solid ${alpha(
                          isPinned ? theme.palette.primary.main : theme.palette.text.secondary,
                          isPinned ? 0.5 : 0.35
                        )}`,
                        backgroundColor: alpha(theme.palette.background.default, isDark ? 0.45 : 0.7),
                        '&:hover': {
                          color: theme.palette.primary.main,
                          borderColor: alpha(theme.palette.primary.main, 0.6),
                        },
                      }}
                      aria-label={isPinned ? 'Unpin chart' : 'Pin chart'}
                    >
                      {isPinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
                    </IconButton>
                  </MuiTooltip>
                )}
              </Box>
            )}
          </Box>

          <Box
            data-testid="timeseries-chart"
            onClick={handleChartWrapperClick}
            sx={{ cursor: onSelectReferenceTime ? 'pointer' : 'default' }}
          >
            <ResponsiveContainer width="100%" height={height}>
              <ChartComponent
                data={mergedWithEmptySeries}
                margin={{ top: 8, right: 12, bottom: 20, left: 16 }}
                onClick={onSelectReferenceTime ? handleChartClick : undefined}
              >
                <defs>
                  {visibleSeries.map((s) => (
                    <linearGradient
                      key={s.id}
                      id={`gradient-${gradientPrefix}-${s.id}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(theme.palette.text.secondary, isDark ? 0.2 : 0.25)}
                  vertical={false}
                />
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={xDomain ?? ['auto', 'auto']}
                  stroke={theme.palette.text.disabled}
                  style={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem' }}
                  tickFormatter={axisTimeFormatter}
                  tickMargin={10}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  minTickGap={50}
                />
                <YAxis
                  stroke={theme.palette.text.disabled}
                  style={{ fontFamily: '"Space Mono", monospace', fontSize: '0.8rem' }}
                  tickLine={false}
                  axisLine={false}
                  width={72}
                  tickMargin={12}
                  tickFormatter={yTickFormatter}
                  allowDecimals={yAxisAllowDecimals}
                  domain={yDomain}
                  padding={yAxisPadding}
                />
                <RechartsTooltip
                  content={
                    <DefaultTooltip
                      timeZone={timezone}
                      valueFormatter={valueFormatter}
                      labelFormatter={(ts: unknown) => formatChartAxisTime(ts as number, timezone)}
                    />
                  }
                  animationDuration={0}
                />

                {visibleSeries.map((s) => {
                  const commonProps: any = {
                    key: s.id,
                    type: 'monotone',
                    dataKey: s.id,
                    name: s.label,
                    stroke: s.color,
                    strokeWidth: s.strokeWidth ?? 2,
                    dot: false,
                    isAnimationActive: true,
                    animationDuration: 750,
                    animationEasing: 'linear',
                    connectNulls: true,
                  };

                  const isArea = (s.type || 'area') === 'area';
                  if (isArea) {
                    return (
                      <Area
                        {...commonProps}
                        stackId={stacked ? 'stack' : undefined}
                        fill={`url(#gradient-${gradientPrefix}-${s.id})`}
                        fillOpacity={1}
                        strokeLinecap="round"
                      />
                    );
                  }

                  return <Line {...commonProps} strokeLinecap="round" />;
                })}
                {referenceInDomain && referenceTimeMs != null && (
                  <ReferenceLine
                    x={referenceTimeMs}
                    stroke={alpha(theme.palette.warning.main, isDark ? 0.95 : 0.85)}
                    strokeWidth={2}
                    strokeDasharray={referenceLocked ? undefined : '5 4'}
                    ifOverflow="visible"
                  />
                )}
              </ChartComponent>
            </ResponsiveContainer>
          </Box>

          {/* Max / average of each visible series across the charted window */}
          {windowStats.length > 0 && (
            <Box
              data-testid="timeseries-window-stats"
              sx={{
                mt: 1.25,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              {windowStats.map((stat) => (
                <Box
                  key={stat.id}
                  sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, whiteSpace: 'nowrap' }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.75rem',
                      color: alpha(stat.color, 0.9),
                    }}
                  >
                    {stat.label}
                  </Typography>
                  {([
                    ['Max', stat.max],
                    ['Avg', stat.avg],
                  ] as Array<[string, number | null]>).map(([statLabel, statValue]) => (
                    <Box
                      key={statLabel}
                      sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Space Mono", monospace',
                          fontSize: '0.7rem',
                          color: theme.palette.text.secondary,
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                        }}
                      >
                        {statLabel}
                      </Typography>
                      <Typography
                        aria-label={`${stat.label} ${statLabel.toLowerCase()}`}
                        sx={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.8rem',
                          color: theme.palette.text.primary,
                          fontWeight: 700,
                        }}
                      >
                        {statValue === null ? '\u2014' : valueFormatter(statValue)}
                        {unit && statValue !== null ? (
                          <Box component="span" sx={{ ml: 0.4, opacity: 0.75 }}>
                            {unit}
                          </Box>
                        ) : null}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}

          {/* Legend (Grafana-style toggle) */}
          {series.length > 1 && (
            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                alignItems: 'center',
              }}
            >
              {series.map((s) => {
                const isHidden = !!hiddenSeries[s.id];
                const last = legendValueById[s.id];
                return (
                  <Box
                    key={s.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleSeries(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSeries(s.id);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: 'pointer',
                      userSelect: 'none',
                      opacity: isHidden ? 0.4 : 1,
                      transition: 'opacity 0.2s ease',
                      '&:hover': { opacity: isHidden ? 0.6 : 0.85 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: s.color,
                        boxShadow: `0 0 10px ${alpha(s.color, 0.45)}`,
                        flex: '0 0 auto',
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: '0.85rem',
                        color: theme.palette.text.secondary,
                        textDecoration: isHidden ? 'line-through' : 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.95rem',
                        color: s.color,
                        fontWeight: 700,
                        textDecoration: isHidden ? 'line-through' : 'none',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {last === null ? '—' : valueFormatter(last)}
                      {unit ? <Box component="span" sx={{ ml: 0.5, opacity: 0.8 }}>{unit}</Box> : null}
                    </Typography>
                  </Box>
                );
              })}
              {legendTotal !== null && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    userSelect: 'none',
                    pl: 1.5,
                    ml: 0.5,
                    borderLeft: `1px solid ${alpha(theme.palette.text.secondary, 0.25)}`,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: '0.85rem',
                      color: theme.palette.text.secondary,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Total
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.95rem',
                      color: theme.palette.text.primary,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {valueFormatter(legendTotal)}
                    {unit ? <Box component="span" sx={{ ml: 0.5, opacity: 0.8 }}>{unit}</Box> : null}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }
);

TimeseriesCard.displayName = 'TimeseriesCard';

